/**
 * Input validation utilities
 */

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email
 */
export function validateEmail(email) {
  if (!email) return false;

  // RFC 5322 compliant email regex (simplified)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Additional checks
  const trimmed = email.trim();
  if (trimmed !== email) return false; // Has whitespace
  if (trimmed.length > 254) return false; // Too long

  return emailRegex.test(trimmed);
}

/**
 * Validate phone number (10-15 digits after normalization)
 * @param {string} phone - Phone number to validate
 * @returns {{valid: boolean, error?: string}} Validation result with error message
 */
export function validatePhone(phone) {
  if (!phone) {
    return { valid: false, error: 'Phone number is required' };
  }

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Check length (10-15 digits for international)
  if (digits.length < 10) {
    return { valid: false, error: 'Phone number must have at least 10 digits' };
  }

  if (digits.length > 15) {
    return { valid: false, error: 'Phone number must not exceed 15 digits' };
  }

  return { valid: true };
}

/**
 * Normalize phone number to digits only
 * @param {string} phone - Phone number to normalize
 * @returns {string} Normalized phone number
 */
export function normalizePhone(phone) {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

/**
 * Format phone number for display
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export function formatPhone(phone) {
  const digits = normalizePhone(phone);

  // US phone format
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // International with US country code
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // Return as-is for other formats
  return phone;
}

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {{valid: boolean, errors: string[]}} Validation result
 */
export function validatePassword(password) {
  const errors = [];

  if (!password) {
    errors.push('Password is required');
  } else {
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validate name (first or last)
 * @param {string} name - Name to validate
 * @returns {boolean} True if valid
 */
export function validateName(name) {
  if (!name) return false;

  const trimmed = name.trim();

  // Check length
  if (trimmed.length < 1 || trimmed.length > 50) return false;

  // Allow letters, spaces, hyphens, apostrophes
  const nameRegex = /^[a-zA-Z\s'-]+$/;

  return nameRegex.test(trimmed);
}

/**
 * Validate NMLS ID
 * @param {string} nmlsId - NMLS ID to validate
 * @param {boolean} required - Whether the field is required
 * @returns {{valid: boolean, error?: string}} Validation result with error message
 */
export function validateNmlsId(nmlsId, required = false) {
  if (!nmlsId) {
    // Optional field by default
    if (required) {
      return { valid: false, error: 'NMLS ID is required' };
    }
    return { valid: true };
  }

  const trimmedId = nmlsId.trim();

  // NMLS IDs are numeric, typically 6-7 digits
  const nmlsRegex = /^\d{4,10}$/;

  if (!nmlsRegex.test(trimmedId)) {
    return { valid: false, error: 'NMLS ID must be 4-10 digits' };
  }

  return { valid: true };
}

/**
 * Validate ZIP code
 * @param {string} zip - ZIP code to validate
 * @returns {boolean} True if valid
 */
export function validateZipCode(zip) {
  if (!zip) return false;

  // US ZIP code (5 digits or 5+4 format)
  const zipRegex = /^\d{5}(-\d{4})?$/;

  return zipRegex.test(zip.trim());
}

/**
 * Sanitize input to prevent XSS
 * @param {string} input - Input to sanitize
 * @returns {string} Sanitized input
 */
export function sanitizeInput(input) {
  if (!input) return '';

  return String(input)
    .replace(/[<>]/g, '') // Remove angle brackets
    .trim();
}

/**
 * Validate required field
 * @param {any} value - Value to check
 * @param {string} fieldName - Field name for error message
 * @returns {{valid: boolean, error: string|null}} Validation result
 */
export function validateRequired(value, fieldName = 'Field') {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return {
      valid: false,
      error: `${fieldName} is required`
    };
  }

  return {
    valid: true,
    error: null
  };
}

/**
 * Validate authentication response from backend
 * @param {object} response - Axios response object
 * @returns {{valid: boolean, error: string|null, isTokenConsumed: boolean}} Validation result
 */
export function validateAuthResponse(response) {
  // Check if response exists and has data
  if (!response || !response.data) {
    return {
      valid: false,
      error: 'Invalid server response',
      isTokenConsumed: false
    };
  }

  // Handle explicit failure responses (backend returns success: false with 200 status)
  if (response.data.success === false) {
    const error = response.data.error || 'Authentication failed';
    const message = response.data.message || error;

    return {
      valid: false,
      error: message,
      isTokenConsumed: error === 'token_already_consumed',
      requiresNewLink: response.data.requires_new_link || false
    };
  }

  // Check for user data in successful responses
  if (!response.data.user) {
    return {
      valid: false,
      error: 'No user data in response',
      isTokenConsumed: false
    };
  }

  // Validate user data structure
  const user = response.data.user;
  if (!user.id || !user.email) {
    return {
      valid: false,
      error: 'Incomplete user data received',
      isTokenConsumed: false
    };
  }

  return {
    valid: true,
    error: null,
    isTokenConsumed: false,
    user: user,
    metadata: {
      isFirstLogin: response.data.is_first_login || false,
      needsOnboarding: response.data.needs_onboarding || false,
      profileCompleted: response.data.profile_completed || false
    }
  };
}

/**
 * Check if an error is retryable
 * @param {Error} error - Error object from axios or custom error
 * @returns {boolean} True if the error is retryable
 */
export function isRetryableError(error) {
  if (!error) return false;

  // Network errors
  if (!error.response && error.code === 'ECONNABORTED') return true;
  if (error.code === 'ETIMEDOUT') return true;
  if (error.code === 'ECONNRESET') return true;

  // Server errors (5xx)
  const status = error.response?.status;
  if (status >= 500 && status < 600) return true;

  // Gateway errors
  if (status === 502 || status === 503 || status === 504) return true;

  // Rate limiting (retry after delay)
  if (status === 429) return true;

  return false;
}

/**
 * Get user-friendly error message from error object
 * @param {Error} error - Error object
 * @returns {string} User-friendly error message
 */
export function getAuthErrorMessage(error) {
  if (!error) return 'An unexpected error occurred';

  // Custom error messages
  if (error.message === 'TOKEN_ALREADY_CONSUMED') {
    return 'This login link has already been used. Please request a new magic link.';
  }

  if (error.message === 'Invalid server response format') {
    return 'The server response was invalid. Please try again or contact support.';
  }

  // HTTP status code based messages
  const status = error.response?.status;

  if (status === 401) {
    const rawDetail = error.response?.data?.detail;
    const detail = typeof rawDetail === 'string' ? rawDetail : JSON.stringify(rawDetail ?? '');

    if (detail.includes('already used') || detail.includes('consumed')) {
      return 'This login link has already been used. Please request a new one.';
    }
    if (detail.includes('expired')) {
      return 'This login link has expired. Please request a new one.';
    }
    return 'Invalid or expired login credentials. Please try again.';
  }

  if (status === 403) {
    return 'You do not have permission to access this resource.';
  }

  if (status === 404) {
    return 'The requested resource was not found.';
  }

  if (status === 422) {
    return error.response?.data?.detail || 'Invalid request data. Please check your input.';
  }

  if (status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }

  if (status === 500) {
    return 'Server error. Please try again in a moment.';
  }

  if (status === 502) {
    return 'Gateway error. The service is temporarily unavailable.';
  }

  if (status === 503) {
    return 'Service temporarily unavailable. Please try again later.';
  }

  if (status === 504) {
    return 'Request timeout. Please check your connection and try again.';
  }

  // Network errors
  if (!error.response) {
    if (error.code === 'ECONNABORTED') return 'Request timeout. Please try again.';
    if (error.code === 'NETWORK_ERROR') return 'Network error. Please check your internet connection.';
    return 'Connection error. Please check your internet connection and try again.';
  }

  // Fallback to backend message or generic error
  return error.response?.data?.detail ||
         error.response?.data?.message ||
         error.message ||
         'Authentication failed. Please try again.';
}
